import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';
import '@theme/size-guide-tables';

/**
 * @typedef {Object} SizeGuideLayerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} [trigger]
 * @property {HTMLElement} [scrollArea]
 * @property {HTMLElement} [switcher]
 * @property {HTMLButtonElement} [switcherButton]
 * @property {HTMLElement} [switcherLabel]
 * @property {HTMLElement} [listbox]
 * @property {HTMLElement[]} [options]
 * @property {HTMLElement[]} [guides]
 */

/**
 * « Guide des tailles » surlayer: a modal dialog holding every published guide, one of them shown.
 *
 * Its dialog sits inside `<size-drawer>` in the DOM (cf. snippets/size-guide-layer.liquid), so clicks
 * and Escape keydowns fired inside the guide bubble up to the size layer, whose own outside-click and
 * keydown handlers would dismiss the layer underneath. Both are stopped here while the guide is open —
 * with `stopPropagation`, not `stopImmediatePropagation`: this element's own handlers must still run.
 *
 * Careful: propagation is the wrong lever against DialogComponent, whose keydown handler is bound to
 * THIS element too (dialog.js, in showDialog). Only `stopImmediatePropagation` reaches it, and only
 * because connectedCallback runs first — see the Escape branch of `#onKeyDown`.
 *
 * It also drives the typology dropdown, a button + `listbox` pair standing in for the `<select>` the
 * créa needs painted. The scroll hint on the size tables lives in each guide's `<size-guide-tables>`
 * wrapper (size-guide-tables.js), shared with the CMS page section.
 *
 * The page only ships the trigger: the dialog (~190 KB, every published guide) is fetched on the first
 * click from `sections/size-guide-dialog.liquid`, so it costs nothing to the PDP or the quick-add. The
 * fetch starts as soon as the trigger is hovered or focused, and the trigger shows a loading state
 * (`aria-busy`) if the click still has to wait for it.
 *
 * @extends {DialogComponent}
 */
class SizeGuideLayer extends DialogComponent {
  // The dialog only exists once fetched (see `#loadDialog`).
  requiredRefs = [];

  /** @type {Promise<void> | null} */
  #loading = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#stopWhileOpen);
    this.addEventListener('keydown', this.#stopWhileOpen);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusout', this.#onFocusOut);
    this.refs.trigger?.addEventListener('pointerenter', this.#prefetch);
    this.refs.trigger?.addEventListener('focus', this.#prefetch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#stopWhileOpen);
    this.removeEventListener('keydown', this.#stopWhileOpen);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.refs.trigger?.removeEventListener('pointerenter', this.#prefetch);
    this.refs.trigger?.removeEventListener('focus', this.#prefetch);
  }

  #prefetch = () => {
    if (!this.refs.dialog) this.#loading ??= this.#loadDialog();
  };

  /** @param {Event} event */
  #stopWhileOpen = (event) => {
    if (this.refs.dialog?.open) event.stopPropagation();
  };

  /** @returns {SizeGuideLayerRefs} */
  get #layerRefs() {
    return /** @type {SizeGuideLayerRefs} */ (this.refs);
  }

  /**
   * Every guide container, shown or hidden. `ref="guides[]"` always yields an array, even for a
   * single match (cf. `#updateRefs` in component.js).
   * @returns {HTMLElement[]}
   */
  get #guides() {
    const { guides } = this.#layerRefs;
    return Array.isArray(guides) ? guides.filter((el) => el instanceof HTMLElement) : [];
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    const { options } = this.#layerRefs;
    return Array.isArray(options) ? options.filter((el) => el instanceof HTMLElement) : [];
  }

  get #isSwitcherOpen() {
    const { listbox } = this.#layerRefs;
    return listbox instanceof HTMLElement && !listbox.hidden;
  }

  /* ─── Dropdown ─────────────────────────────────────────────────────────── */

  /** Declarative handler on the trigger (`on:click`). */
  toggleSwitcher() {
    if (this.#isSwitcherOpen) this.#closeSwitcher(true);
    else this.#openSwitcher();
  }

  /**
   * Declarative handler on each option (`on:click`). The event target is proxied to the element
   * carrying the attribute, so it is the `<li>` itself even when the click landed on its label.
   * @param {Event} event
   */
  pickTypology(event) {
    const { target } = event;
    if (target instanceof HTMLElement) this.#pickOption(target);
  }

  /** Opens the list and puts focus on the selected option, the way a native select does. */
  #openSwitcher() {
    const { listbox, switcherButton } = this.#layerRefs;
    if (!(listbox instanceof HTMLElement) || !(switcherButton instanceof HTMLElement)) return;

    listbox.hidden = false;
    switcherButton.setAttribute('aria-expanded', 'true');

    const selected = this.#options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.#focusOption(selected === -1 ? 0 : selected);
  }

  /**
   * @param {boolean} returnFocus - True whenever the customer dismissed the list themselves (Escape,
   * a pick, a second click): focus must not be left on a node that just left the accessibility tree.
   * False when they moved on by themselves (Tab, a click elsewhere), where stealing focus back would
   * fight them.
   */
  #closeSwitcher(returnFocus) {
    const { listbox, switcherButton } = this.#layerRefs;
    if (!(listbox instanceof HTMLElement) || listbox.hidden) return;

    listbox.hidden = true;
    switcherButton?.setAttribute('aria-expanded', 'false');
    if (returnFocus) switcherButton?.focus();
  }

  /** @param {number} index - Clamped rather than wrapped, like a native select. */
  #focusOption(index) {
    const options = this.#options;
    if (!options.length) return;

    const bounded = Math.min(Math.max(index, 0), options.length - 1);
    options[bounded]?.focus();
  }

  /** @param {HTMLElement} option */
  #pickOption(option) {
    const { switcherLabel } = this.#layerRefs;
    const handle = option.dataset.sizeGuideHandle;
    if (!handle) return;

    for (const item of this.#options) {
      item.setAttribute('aria-selected', String(item === option));
    }

    const label = option.querySelector('.size-guide-layer__option-label')?.textContent?.trim();
    if (switcherLabel instanceof HTMLElement && label) switcherLabel.textContent = label;

    this.#showGuide(handle);
    this.#closeSwitcher(true);
  }

  /**
   * Shows the picked typology and hides the others. `hidden` rather than a class: it takes the guides
   * out of the accessibility tree as well as the render, so their lazy illustrations stay unfetched
   * until shown.
   * @param {string} handle
   */
  #showGuide(handle) {
    const { scrollArea } = this.#layerRefs;

    for (const guide of this.#guides) {
      guide.hidden = guide.dataset.sizeGuideHandle !== handle;
    }

    // The previous guide's scroll offset means nothing in the new one.
    if (scrollArea instanceof HTMLElement) scrollArea.scrollTop = 0;
  }

  /**
   * The whole keyboard contract of a select, which a `<div>` gives none of.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const { switcherButton } = this.#layerRefs;
    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const onButton = target === switcherButton;
    const onOption = target.getAttribute('role') === 'option';
    if (!onButton && !onOption) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        // Also the way to open from the trigger — otherwise the arrows scroll the layer instead.
        event.preventDefault();
        if (!this.#isSwitcherOpen) {
          this.#openSwitcher();
          return;
        }
        this.#focusOption(this.#options.indexOf(target) + (event.key === 'ArrowDown' ? 1 : -1));
        break;

      case 'Home':
      case 'End':
        if (!this.#isSwitcherOpen) return;
        event.preventDefault();
        this.#focusOption(event.key === 'Home' ? 0 : this.#options.length - 1);
        break;

      case 'Enter':
      case ' ':
        // On the trigger these already fire a native click, which toggles the list.
        if (onButton) return;
        event.preventDefault();
        this.#pickOption(target);
        break;

      case 'Escape':
        if (!this.#isSwitcherOpen) return;
        // Escape must dismiss one layer of UI at a time. `preventDefault` alone does not achieve
        // that: it only cancels the <dialog>'s native `cancel`, while DialogComponent's own keydown
        // handler — bound to THIS element in showDialog — still runs and closes the whole surlayer.
        // Since connectedCallback registered this handler first, stopping the rest of the chain
        // reaches it. Same pattern, same reason, as header-menu-dialog.js.
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#closeSwitcher(true);
        break;

      case 'Tab':
        // Not prevented: the list is a dropdown, not a trap. It just must not stay open behind.
        this.#closeSwitcher(false);
        break;
    }
  };

  /**
   * Closes the list as soon as focus leaves the switcher, which covers clicking anywhere else in the
   * page too: a click on a non-focusable area blurs the focused option, and `relatedTarget` is then
   * null. A click on the trigger itself keeps focus inside, and its own handler toggles.
   *
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const { switcher } = this.#layerRefs;
    if (!this.#isSwitcherOpen || !(switcher instanceof HTMLElement)) return;

    const next = event.relatedTarget;
    if (next instanceof Node && switcher.contains(next)) return;

    this.#closeSwitcher(false);
  };

  /* ─── Dialog ───────────────────────────────────────────────────────────── */

  async openLayer() {
    if (!this.refs.dialog) {
      const { trigger } = this.#layerRefs;
      trigger?.setAttribute('aria-busy', 'true');
      try {
        await (this.#loading ??= this.#loadDialog());
      } finally {
        trigger?.removeAttribute('aria-busy');
      }
    }
    if (this.refs.dialog) this.showDialog();
  }

  async #loadDialog() {
    try {
      const url = new URL(this.dataset.productUrl ?? '', window.location.origin);
      const html = await sectionRenderer.getSectionHTML('size-guide-dialog', true, url);
      const dialog = new DOMParser().parseFromString(html, 'text/html').querySelector('dialog');
      if (!dialog) return;

      this.append(dialog);
      // Re-run the connect lifecycle: refs, and DialogComponent's scroll hint on the new scroll area.
      this.disconnectedCallback();
      this.connectedCallback();
    } finally {
      this.#loading = null;
    }
  }

  closeLayer() {
    // The dropdown must not survive its layer: reopening the guide would show it expanded.
    this.#closeSwitcher(false);
    this.closeDialog();
  }
}

if (!customElements.get('size-guide-layer')) {
  customElements.define('size-guide-layer', SizeGuideLayer);
}
